import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import { importOnboardingSettingsService } from '../../services/importOnboardingSettings';

type ImportOnboardingState = {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  saveStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  seenAt: string | null;
  lastLoadedUserId: string | null;
};

const IMPORT_ONBOARDING_VERSION = 1;

const normalizeSeenAt = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length) {
      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.valueOf())) return parsed.toISOString();
    }
  }
  return null;
};

const ensureImportOnboardingState = (state: UiState): ImportOnboardingState => {
  if (!state.importOnboarding) {
    state.importOnboarding = {
      status: 'idle',
      saveStatus: 'idle',
      error: null,
      seenAt: null,
      lastLoadedUserId: null,
    };
  }
  return state.importOnboarding;
};

type LoadImportOnboardingReject = { message: string; userId: string | null };

export const loadImportOnboardingSetting = createAsyncThunk<
  { seenAt: string | null; userId: string | null },
  void,
  { state: RootState; rejectValue: LoadImportOnboardingReject }
>('ui/loadImportOnboardingSetting', async (_, { getState, rejectWithValue }) => {
  const userId = getState().auth.user?.id ?? null;
  if (!userId) {
    return rejectWithValue({ message: 'Missing user context', userId });
  }
  try {
    const setting = await importOnboardingSettingsService.fetch();
    return { seenAt: normalizeSeenAt(setting?.seenAt) ?? null, userId };
  } catch (error: any) {
    const message =
      error?.response?.data?.error ?? error?.message ?? 'Failed to load onboarding setting';
    return rejectWithValue({ message, userId });
  }
}, {
  condition: (_arg, { getState }) => {
    const state = getState() as RootState;
    const userId = state.auth.user?.id ?? null;
    if (!userId) return false;
    const { importOnboarding } = state.ui;
    const alreadyAttemptedForUser =
      importOnboarding.lastLoadedUserId === userId &&
      (importOnboarding.status === 'succeeded' || importOnboarding.status === 'failed');
    return !alreadyAttemptedForUser;
  }
});

export const markImportOnboardingSeen = createAsyncThunk<
  { seenAt: string; userId: string | null },
  { forceSeenAt?: string } | void,
  { state: RootState }
>('ui/markImportOnboardingSeen', async (arg, { getState, rejectWithValue }) => {
  const state = getState();
  const userId = state.auth.user?.id ?? null;
  if (!userId) {
    return rejectWithValue('Missing user context');
  }
  const seenAt = normalizeSeenAt(arg?.forceSeenAt) ?? new Date().toISOString();

  try {
    await importOnboardingSettingsService.markSeen({ version: IMPORT_ONBOARDING_VERSION, seenAt });
    return { seenAt, userId };
  } catch (error: any) {
    const message = error?.response?.data?.error ?? error?.message ?? 'Failed to mark onboarding seen';
    return rejectWithValue(message);
  }
});

export interface UiState {
  queueCollapsed: boolean;
  devicesCollapsed: boolean;
  detailsCollapsed: boolean;
  libraryCollapsed: boolean;
  libraryExpanded: boolean;
  loginTooltipOpen: boolean;
  loginButtonOpen: boolean;
  loginModalItem: string | null;
  importOnboardingOpen: boolean;
  importOnboarding: ImportOnboardingState;
  importCompletionOpen: boolean;
}

const initialState: UiState = {
  queueCollapsed: true,
  devicesCollapsed: true,
  detailsCollapsed: true,
  libraryCollapsed: false,
  libraryExpanded: false,
  loginTooltipOpen: false,
  loginButtonOpen: false,
  loginModalItem: null,
  importOnboardingOpen: false,
  importOnboarding: {
    status: 'idle',
    saveStatus: 'idle',
    error: null,
    seenAt: null,
    lastLoadedUserId: null,
  },
  importCompletionOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openLoginButton(state) {
      state.loginButtonOpen = true;
      state.loginModalItem = null;
      state.loginTooltipOpen = false;
    },
    closeLoginButton(state) {
      state.loginButtonOpen = false;
    },
    openLoginModal(state, action: PayloadAction<string>) {
      state.loginModalItem = action.payload;
      state.loginButtonOpen = false;
      state.loginTooltipOpen = false;
    },
    closeLoginModal(state) {
      state.loginModalItem = null;
    },
    openLoginTooltip(state) {
      state.loginTooltipOpen = true;
      state.loginModalItem = null;
      state.loginButtonOpen = false;
    },
    closeLoginTooltip(state) {
      state.loginTooltipOpen = false;
    },
    collapseDetails(state) {
      state.detailsCollapsed = true;
    },
    toggleDetails(state) {
      state.queueCollapsed = true;
      state.devicesCollapsed = true;
      state.libraryExpanded = false;
      state.detailsCollapsed = !state.detailsCollapsed;
    },
    collapseLibrary(state) {
      state.libraryCollapsed = true;
      state.libraryExpanded = false;
    },
    openLibrary(state) {
      state.libraryCollapsed = false;
      state.libraryExpanded = false;
    },
    toggleLibrary(state) {
      if (state.libraryCollapsed) {
        state.libraryCollapsed = false;
        state.libraryExpanded = false;
      } else {
        state.libraryCollapsed = true;
        state.libraryExpanded = false;
      }
    },
    expandLibrary(state) {
      state.libraryCollapsed = false;
      state.libraryExpanded = true;
    },
    shrinkLibrary(state) {
      state.libraryExpanded = false;
    },
    collapseQueue(state) {
      state.queueCollapsed = true;
    },
    toggleQueue(state) {
      state.detailsCollapsed = true;
      state.devicesCollapsed = true;
      state.libraryExpanded = false;
      state.queueCollapsed = !state.queueCollapsed;
    },
    openQueue(state) {
      state.detailsCollapsed = true;
      state.devicesCollapsed = true;
      state.libraryExpanded = false;
      state.queueCollapsed = false;
    },
    collapseDevices(state) {
      state.devicesCollapsed = true;
    },
    toggleDevices(state) {
      state.detailsCollapsed = true;
      state.queueCollapsed = true;
      state.libraryExpanded = false;
      state.devicesCollapsed = !state.devicesCollapsed;
    },
    openImportOnboarding(state) {
      state.importOnboardingOpen = true;
    },
    closeImportOnboarding(state) {
      state.importOnboardingOpen = false;
    },
    openImportCompletion(state) {
      state.importCompletionOpen = true;
    },
    closeImportCompletion(state) {
      state.importCompletionOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadImportOnboardingSetting.pending, (state) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.status = 'loading';
        onboarding.error = null;
      })
      .addCase(loadImportOnboardingSetting.fulfilled, (state, action) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.status = 'succeeded';
        onboarding.error = null;
        onboarding.seenAt = action.payload.seenAt ?? null;
        onboarding.lastLoadedUserId = action.payload.userId ?? null;
      })
      .addCase(loadImportOnboardingSetting.rejected, (state, action) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.status = 'failed';
        const payload = action.payload as LoadImportOnboardingReject | undefined;
        onboarding.error =
          payload?.message ??
          (typeof action.payload === 'string'
            ? action.payload
            : action.error?.message ?? 'Failed to load onboarding setting.');
        onboarding.lastLoadedUserId = payload?.userId ?? onboarding.lastLoadedUserId;
      })
      .addCase(markImportOnboardingSeen.pending, (state, action) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.saveStatus = 'pending';
        const nowIso = new Date().toISOString();
        if (action.meta.arg?.forceSeenAt) {
          onboarding.seenAt = action.meta.arg.forceSeenAt;
        } else {
          onboarding.seenAt = onboarding.seenAt ?? nowIso;
        }
      })
      .addCase(markImportOnboardingSeen.fulfilled, (state, action) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.saveStatus = 'succeeded';
        onboarding.error = null;
        onboarding.seenAt = action.payload.seenAt ?? onboarding.seenAt;
        onboarding.lastLoadedUserId = action.payload.userId ?? onboarding.lastLoadedUserId;
      })
      .addCase(markImportOnboardingSeen.rejected, (state, action) => {
        const onboarding = ensureImportOnboardingState(state);
        onboarding.saveStatus = 'failed';
        onboarding.error =
          typeof action.payload === 'string'
            ? action.payload
            : action.error?.message ?? 'Failed to mark onboarding seen.';
      });
  },
});

export const isRightLayoutOpen = createSelector(
  [
    (state: RootState) => state.ui.queueCollapsed,
    (state: RootState) => state.ui.devicesCollapsed,
    (state: RootState) => state.ui.detailsCollapsed,
  ],
  (queueCollapsed, devicesCollapsed, detailsCollapsed) => {
    return !queueCollapsed || !devicesCollapsed || !detailsCollapsed;
  }
);

export const getLibraryCollapsed = createSelector(
  [(state: RootState) => state.ui.libraryCollapsed, (state: RootState) => state.auth.user],
  (libraryCollapsed, user) => {
    return !user ? false : libraryCollapsed;
  }
);

export const getLibraryExpanded = createSelector(
  [(state: RootState) => state.ui.libraryExpanded, (state: RootState) => state.auth.user],
  (libraryExpanded, user) => {
    return !user ? false : libraryExpanded;
  }
);

export const uiActions = {
  ...uiSlice.actions,
  loadImportOnboardingSetting,
  markImportOnboardingSeen,
};

export default uiSlice.reducer;
